import React, { forwardRef, useImperativeHandle } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withRepeat,
  cancelAnimation,
  withTiming,
} from 'react-native-reanimated';

import {
  InteractiveBox,
  InteractiveForwardRefProps,
} from '@/components/interactive';

const SIZE = 100.0;

const handleRotation = (progress: Animated.SharedValue<number>) => {
  'worklet';

  return `${progress.value * 2 * Math.PI}rad`;
};

const AnimatedSquare = forwardRef((_, ref) => {
  const progress = useSharedValue(1);
  const scale = useSharedValue(2);

  const reanimatedStyle = useAnimatedStyle(() => {
    return {
      opacity: progress.value,
      borderRadius: (progress.value * SIZE) / 2,
      transform: [{ scale: scale.value }, { rotate: handleRotation(progress) }],
    };
  }, []);

  const play = () => {
    reset();
    setTimeout(() => {
      progress.value = withRepeat(withSpring(0.5), -1, true);
      scale.value = withRepeat(withSpring(1), -1, true);
    }, 100);
  };

  const reset = () => {
    cancelAnimation(progress);
    cancelAnimation(scale);
    progress.value = 1;
    scale.value = 2;
  };

  useImperativeHandle(ref, () => ({
    play,
    reset,
    pause: () => {},
  }));

  return (
    <View style={styles.container}>
      <Animated.View
        style={[
          { height: SIZE, width: SIZE, backgroundColor: 'blue' },
          reanimatedStyle,
        ]}
      />
    </View>
  );
});

const AnimatedSquareOpacity = forwardRef((_, ref) => {
  const progress = useSharedValue(0);

  const reanimatedStyle = useAnimatedStyle(() => {
    return {
      opacity: progress.value,
    };
  }, []);

  const play = () => {
    reset();
    setTimeout(() => {
      progress.value = withTiming(1, { duration: 500 });
    }, 100);
  };

  const reset = () => {
    cancelAnimation(progress);
    progress.value = 0;
  };

  useImperativeHandle(ref, () => ({
    play,
    reset,
    pause: () => {},
  }));

  return (
    <View style={styles.container}>
      <Animated.View
        style={[
          { height: SIZE, width: SIZE, backgroundColor: 'blue' },
          reanimatedStyle,
        ]}
      />
    </View>
  );
});

type AnimatedSquareOpacityAndScaleProps = {
  scaleAnimationType: 'spring' | 'timing';
  fromOpacity?: number;
  toOpacity?: number;
  showBorderRadiusAnimation?: boolean;
};

const AnimatedSquareOpacityAndScale = forwardRef<
  InteractiveForwardRefProps,
  AnimatedSquareOpacityAndScaleProps
>(
  (
    {
      scaleAnimationType,
      fromOpacity = 0,
      toOpacity = 1,
      showBorderRadiusAnimation = false,
    },
    ref
  ) => {
    const progress = useSharedValue(fromOpacity);
    const scale = useSharedValue(1);

    const withScaleAnimationType =
      scaleAnimationType === 'spring' ? withSpring : withTiming;

    const reanimatedStyle = useAnimatedStyle(() => {
      return {
        opacity: progress.value,
        borderRadius: showBorderRadiusAnimation
          ? (progress.value * SIZE) / 2
          : 0,
        transform: [{ scale: scale.value }],
      };
    }, []);

    const play = () => {
      reset();
      setTimeout(() => {
        progress.value = withTiming(toOpacity, { duration: 500 });
        scale.value = withScaleAnimationType(2);
      }, 100);
    };

    const reset = () => {
      cancelAnimation(progress);
      cancelAnimation(scale);
      progress.value = fromOpacity;
      scale.value = 1;
    };

    useImperativeHandle(ref, () => ({
      play,
      reset,
      pause: () => {},
    }));

    return (
      <View style={styles.container}>
        <Animated.View
          style={[
            { height: SIZE, width: SIZE, backgroundColor: 'blue' },
            reanimatedStyle,
          ]}
        />
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flex: 1,
    backgroundColor: 'white',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const AnimatedSquareOpacityAndScaleSpring =
  forwardRef<InteractiveForwardRefProps>((_, ref) => {
    return (
      <AnimatedSquareOpacityAndScale
        ref={ref}
        scaleAnimationType="spring"
        fromOpacity={1}
        toOpacity={0.5}
      />
    );
  });

const AnimatedSquareOpacityAndScaleBorderRadiusSpring =
  forwardRef<InteractiveForwardRefProps>((_, ref) => {
    return (
      <AnimatedSquareOpacityAndScale
        ref={ref}
        scaleAnimationType="spring"
        fromOpacity={1}
        toOpacity={0.5}
        showBorderRadiusAnimation={true}
      />
    );
  });

const AnimatedSquareOpacityAndScaleTiming =
  forwardRef<InteractiveForwardRefProps>((_, ref) => {
    return (
      <AnimatedSquareOpacityAndScale
        ref={ref}
        scaleAnimationType="timing"
        fromOpacity={0}
        toOpacity={1}
      />
    );
  });

type AnimatedSquareOpacityAndScaleDemoProps = {
  scaleAnimationType: 'spring' | 'timing';
  showBorderRadiusAnimation?: boolean;
};

export const AnimatedSquareOpacityAndScaleDemo = forwardRef<
  InteractiveForwardRefProps,
  AnimatedSquareOpacityAndScaleDemoProps
>(({ scaleAnimationType, showBorderRadiusAnimation = false }, ref) => {
  return (
    <InteractiveBox
      children={
        scaleAnimationType === 'spring'
          ? showBorderRadiusAnimation
            ? AnimatedSquareOpacityAndScaleBorderRadiusSpring
            : AnimatedSquareOpacityAndScaleSpring
          : AnimatedSquareOpacityAndScaleTiming
      }
      // @ts-expect-error
      ref={ref}
      withPlay
    />
  );
});

export const FinalAnimatedSquareDemo = () => {
  return <InteractiveBox children={AnimatedSquare} withPlay withReset />;
};

export const AnimatedSquareOpacityDemo = () => {
  return <InteractiveBox children={AnimatedSquareOpacity} withPlay />;
};
